import { getDefaultLocale } from './genericHelpers';

const SUPPORTED_CHAT_LANGUAGES = new Set(['en', 'el', 'de', 'ar']);
const AI_RELATED_RESOURCE_LANGUAGE = 'en';

export const normalizeLocaleTag = locale => {
  const raw = String(locale || '').trim();
  if (!raw) return null;
  return raw.replace('_', '-');
};

export const normalizeChatLanguage = value => {
  const normalized = normalizeLocaleTag(value)?.toLowerCase() || '';
  if (!normalized) return null;
  if (normalized === 'gr' || normalized.startsWith('gr-')) return 'el';
  const base = normalized.split('-')[0];
  return SUPPORTED_CHAT_LANGUAGES.has(base) ? base : null;
};

export const resolveChatLanguage = ({ preferredLang, appLocale } = {}) => {
  return normalizeChatLanguage(preferredLang) || normalizeChatLanguage(appLocale) || 'en';
};

export const resolveAllowedCardLanguages = chatLanguage => {
  return [AI_RELATED_RESOURCE_LANGUAGE];
};

export const toChatLocale = chatLanguage => {
  const normalized = resolveChatLanguage({ preferredLang: chatLanguage });
  if (normalized === 'el') return 'el-GR';
  if (normalized === 'de') return 'de';
  if (normalized === 'ar') return 'ar';
  return 'en';
};

export const detectMessageLanguage = messageText => {
  const text = String(messageText || '');
  if (!text.trim()) return null;
  if (/[\u0600-\u06FF\u0750-\u077F\u08A0-\u08FF]/.test(text)) return 'ar';
  if (/[\u0370-\u03FF]/.test(text)) return 'el';
  if (/[äöüß]/i.test(text)) return 'de';
  if (/[a-z]/i.test(text)) return 'en';
  return null;
};

export const getLocalTimeZone = () => {
  if (typeof Intl === 'undefined' || typeof Intl.DateTimeFormat !== 'function') {
    return null;
  }
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || null;
  } catch (_) {
    return null;
  }
};

export const buildAiLanguagePayload = ({
  preferredLang,
  appLocale,
  timezone,
  messageText,
} = {}) => {
  const resolvedAppLocale = normalizeLocaleTag(appLocale) || getDefaultLocale() || 'en';
  const resolvedChatLanguage = resolveChatLanguage({
    preferredLang,
    appLocale: resolvedAppLocale,
  });
  const resolvedTimeZone = String(timezone || getLocalTimeZone() || '').trim() || null;
  const messageLanguage = detectMessageLanguage(messageText) || resolvedChatLanguage;
  const locale = toChatLocale(resolvedChatLanguage);
  const allowedCardLanguages = resolveAllowedCardLanguages(resolvedChatLanguage);

  return {
    locale,
    appLocale: resolvedAppLocale,
    deviceLocale: resolvedAppLocale,
    chatLanguage: resolvedChatLanguage,
    conversationPreferredLanguage: resolvedChatLanguage,
    preferredLanguage: resolvedChatLanguage,
    timezone: resolvedTimeZone,
    messageLanguage,
    languagePrefs: [resolvedChatLanguage],
    languagePreferences: [resolvedChatLanguage],
    allowedCardLanguages,
    cardLanguageAllowlist: allowedCardLanguages,
  };
};
